import React from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Intervention,
  InterventionScopeNameEnum,
  useDashboardTaskShortQuery,
} from 'graphql-common';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import Trans from '@lib/components/Trans/Trans';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import { materialSymbolsOutlined } from '@lib/enums/common';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import useSearchParam from '@lib/hooks/useSearchParam';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import useActions from 'routes/Interventions/utils/useActions';
import { useLocation, useParams } from 'react-router-dom';
import useModalManagement from 'routes/Interventions/utils/useModalManagement';
import getTableActionsMenu from 'routes/Interventions/utils/getTableActionsMenu';
import getTableActionsForSelected from 'routes/Interventions/utils/getTableActionsForSelected';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import { GetUrlParams } from '@lib/enums/urls';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import TaskMenuBar from 'components/TaskMenuBar/TaskMenuBar';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import { IconName } from '@lib/components/Modal/enums';
import getTableColumns from './utils/getInterventionsTableColumns';

export default function TaskInterventionsContainer() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const [searchQuery] = useSearchParam();

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.InterventionsManagement) ===
    AccessLevelEnum.Read;

  const useBulkSelectResult = useTableItemsBulkSelect<Intervention>();
  const useModalManagementResult = useModalManagement({ useBulkSelectResult });

  const taskQuery = useDashboardTaskShortQuery({
    variables: { id },
    skip: !id,
    fetchPolicy: 'network-only',
  });
  const { loading: taskLoading } = taskQuery;
  const task = _get(taskQuery, ['data', 'data']) || undefined;
  const name = task?.name || '';
  const groupKey = task?.groupKey || '';
  const canInterventionCreate = task?.canInterventionCreate?.value;

  const useInterventionActionsResult = useActions({
    taskId: groupKey,
    useModalManagementResult,
    useBulkSelectResult,
    searchQuery,
    pathname,
    search,
    skipInterventionsFetch: !task?.id,
    interventionScope: InterventionScopeNameEnum.All,
  });

  const {
    interventionsQueryHook,
    onCloseModal,
    onConfirmedInterventionDestroy,
    openCreateInterventionPage,
    onConfirmedInterventionClose,
  } = useInterventionActionsResult;

  const { totalCount } = interventionsQueryHook;
  const bulkSelectedItemsCount = totalCount
    ? useBulkSelectResult.getSelectedItemsCount(totalCount)
    : 0;

  const tableColumns = getTableColumns({
    onView: useInterventionActionsResult.openViewInterventionPage,
    onStatusChangeHandler: useInterventionActionsResult.onStatusChangeHandler,
    t,
    isViewOnly,
  });
  const tableActionsMenu = getTableActionsMenu({
    onView: useInterventionActionsResult.openViewInterventionPage,
    onEdit: useInterventionActionsResult.openEditInterventionPage,
    onDelete: useInterventionActionsResult.openInterventionDestroyModal,
    onClose: useInterventionActionsResult.openInterventionCloseModal,
    isViewOnly,
    t,
  });
  const tableActionsForSelected = getTableActionsForSelected({
    onBulkClose: useModalManagementResult.openInterventionCloseModal,
    selectedItemsCount: useBulkSelectResult.getSelectedItemsCount(totalCount),
    isViewOnly,
    t,
  });

  const onCreateAssetIntervention = () => {
    openCreateInterventionPage({
      [GetUrlParams.AssetId]: task?.assetId,
      [GetUrlParams.SiteAreaId]: task?.siteAreaId,
      [GetUrlParams.SiteId]: task?.siteId,
      [GetUrlParams.TaskId]: id,
    });
  };

  const emptyState = (
    <ListEmptyState
      title={t('no-interventions-yet')}
      text={<Trans i18nKey="no-interventions-text" />}
      actions={
        isViewOnly
          ? []
          : [
              <Button
                key="create"
                buttonText={t('create-intervention')}
                buttonType={ButtonTypes.primaryFilled}
                leftIcon="add"
                onClick={onCreateAssetIntervention}
                leftIconClassName={materialSymbolsOutlined}
                disabled={!canInterventionCreate}
              />,
            ]
      }
    />
  );

  if (interventionsQueryHook.error) {
    return (
      <ErrorPage
        status={getStatusFromError(interventionsQueryHook.error)}
        description={interventionsQueryHook.error.message}
      />
    );
  }

  return (
    <>
      <BreadCrumbs
        data={getBreadCrumbsDataByKey({ pathname, t, entity: task })}
      />
      <PageBody withoutHeader>
        <TaskMenuBar id={id} />
        {taskLoading || interventionsQueryHook.firstLoading ? (
          <CircleLoader />
        ) : (
          <WhiteBox>
            <DashboardTitleHeader
              title={name}
              actions={
                isViewOnly ? (
                  <ViewOnlyChip key="view" />
                ) : (
                  <Button
                    key="add"
                    buttonText={t('create-intervention')}
                    buttonType={ButtonTypes.primaryFilled}
                    onClick={onCreateAssetIntervention}
                    leftIcon="add"
                    leftIconClassName={materialSymbolsOutlined}
                    disabled={!canInterventionCreate}
                  />
                )
              }
            />
            {(!!interventionsQueryHook.collection?.length || searchQuery) && (
              <Search
                placeholder={t('asset-interventions-search-placeholder')}
              />
            )}
            <Table
              actionsForSelected={tableActionsForSelected}
              actionsMenu={tableActionsMenu}
              columns={tableColumns}
              data={interventionsQueryHook.collection}
              emptyState={emptyState}
              id="interventions"
              loading={interventionsQueryHook.loading}
              totalEntries={interventionsQueryHook.totalCount}
              useBulkSelectResult={useBulkSelectResult}
            />
          </WhiteBox>
        )}
      </PageBody>
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="asset-documentation-delete-text" />}
        icon={IconName.Delete}
        isOpen={useModalManagementResult.isInterventionDestroyModalOpened}
        onClose={onCloseModal}
        onConfirm={onConfirmedInterventionDestroy}
        title={t('are-you-sure')}
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-close')}
        description={
          <Trans
            i18nKey={
              bulkSelectedItemsCount > 1
                ? 'interventions-close-text'
                : 'intervention-close-text'
            }
          />
        }
        icon={IconName.Warning}
        isOpen={useModalManagementResult.isInterventionCloseModalOpened}
        onClose={useModalManagementResult.closeAllModals}
        onConfirm={onConfirmedInterventionClose}
        title={t('are-you-sure')}
      />
    </>
  );
}
